/**
 * ConnectToProjectPanel - linking a resource to projects, in a side panel.
 *
 * Replaces the hover dropdown `ProjectBadges` used to open: a `w-56 max-h-48`
 * absolutely-positioned menu behind a full-screen click-catcher, with no
 * search, no project detail, no way to see or undo what was already linked,
 * and an overflow list that only appeared on hover. On a matter with more than
 * a handful of projects it was unusable.
 *
 * This is the app's one panel system (`GlobalSidePanel`), so it inherits the
 * backdrop, the slide, the close affordance and the escape behaviour. It shows
 * both halves of the relationship - what this resource is already linked to
 * (with unlink) and what it can still be linked to (searchable, multi-select)
 * - because "add to project" is really "manage this resource's projects".
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import {
  PiCheckLight,
  PiLinkBreakLight,
  PiProjectorScreenChartLight,
} from 'react-icons/pi';
import { useAppDispatch } from '../../store';
import {
  addResourceToProject,
  fetchLinkableProjects,
  fetchProjectsByResource,
  removeResourceFromProject,
} from '../../store/actions/Projects/ProjectActions';
import Button from '../shared/TailwindComponents/Button';
import SearchInput from '../shared/TailwindComponents/SearchInput';
import Skeleton from '../shared/TailwindComponents/Skeleton';
import EmptyState from '../shared/TailwindComponents/EmptyState';
import Badge from '../shared/TailwindComponents/Badge';
import { Well } from '../shared/brand/Field';
import { formatDate } from '../../utils/functions';

export interface ConnectToProjectPanelProps {
  resourceType: string;
  resourceUuid: string;
  /** Called after any link/unlink so the badges behind the panel refresh. */
  onChanged?: () => void;
  onClose: () => void;
}

const ConnectToProjectPanel: React.FC<ConnectToProjectPanelProps> = ({
  resourceType,
  resourceUuid,
  onChanged,
  onClose,
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const [linked, setLinked] = useState<any[]>([]);
  const [linkable, setLinkable] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState<string[]>([]);

  const load = useCallback(async () => {
    const [current, all] = await Promise.all([
      dispatch(fetchProjectsByResource(resourceType, resourceUuid)),
      dispatch(fetchLinkableProjects()),
    ]);
    const currentList = Array.isArray(current) ? current : [];
    const linkedUuids = new Set(currentList.map((project: any) => project.uuid));
    setLinked(currentList);
    setLinkable((Array.isArray(all) ? all : []).filter((project: any) => !linkedUuids.has(project.uuid)));
    setSelected([]);
    setIsLoading(false);
  }, [dispatch, resourceType, resourceUuid]);

  useEffect(() => { load(); }, [load]);

  const results = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return linkable;
    return linkable.filter((project: any) =>
      `${project.title || ''} ${project.description || ''}`.toLowerCase().includes(needle)
    );
  }, [linkable, query]);

  const toggle = (uuid: string) => {
    setSelected((prev) =>
      prev.includes(uuid) ? prev.filter((id) => id !== uuid) : [...prev, uuid]
    );
  };

  const handleLink = async () => {
    if (selected.length === 0) return;
    setIsSaving(true);
    try {
      // Sequential, not Promise.all: each link fires its own feedback toast,
      // and the backend counts links per project.
      for (const uuid of selected) {
        await dispatch(addResourceToProject(uuid, resourceType, resourceUuid));
      }
      await load();
      onChanged?.();
    } finally {
      setIsSaving(false);
    }
  };

  const handleUnlink = async (uuid: string) => {
    setIsSaving(true);
    try {
      await dispatch(removeResourceFromProject(uuid, resourceType, resourceUuid));
      await load();
      onChanged?.();
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return <Skeleton variant="list" rows={6} />;
  }

  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 min-h-0 overflow-y-auto custom-scrollbar-main px-5 py-4 space-y-6">
        {/* ── Already linked ── */}
        <section>
          <h4 className="text-[11px] font-medium text-gray-400 dark:text-gray-500 mb-2">
            {t('Linked projects')} {linked.length > 0 && `(${linked.length})`}
          </h4>
          {linked.length === 0 ? (
            <p className="text-sm text-gray-400 dark:text-gray-500">
              {t('Not linked to any project yet.')}
            </p>
          ) : (
            <div className="space-y-2">
              {linked.map((project: any) => (
                <Well key={project.uuid} className="flex items-center gap-3 px-3 py-2">
                  <PiProjectorScreenChartLight className="h-4 w-4 flex-shrink-0 text-fg-muted" />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-gray-700 dark:text-gray-200 truncate">
                      {project.title}
                    </p>
                    {project.status && (
                      <Badge color="gray" className="mt-0.5 capitalize">{project.status}</Badge>
                    )}
                  </div>
                  <Button
                    variant="outline"
                    size="small"
                    disabled={isSaving}
                    onClick={() => handleUnlink(project.uuid)}
                    icon={<PiLinkBreakLight className="h-4 w-4" />}
                  >
                    {t('Unlink')}
                  </Button>
                </Well>
              ))}
            </div>
          )}
        </section>

        {/* ── Available to link ── */}
        <section>
          <div className="flex items-center justify-between gap-3 mb-2">
            <h4 className="text-[11px] font-medium text-gray-400 dark:text-gray-500">
              {t('Add to project')}
            </h4>
            {selected.length > 0 && (
              <span className="text-[11px] text-fg-muted">
                {selected.length} {t('selected')}
              </span>
            )}
          </div>

          <SearchInput
            wrapperClassName="w-full mb-3"
            size="small"
            value={query}
            onChange={setQuery}
            placeholder={t('Search projects')}
          />

          {results.length === 0 ? (
            <EmptyState
              icon={<PiProjectorScreenChartLight />}
              headline={query ? t('No projects match your search') : t('No projects available')}
              subtext={
                query
                  ? t('Try a different search term.')
                  : t('Every project you can link to is already linked.')
              }
            />
          ) : (
            <div className="space-y-2">
              {results.map((project: any) => {
                const isSelected = selected.includes(project.uuid);
                return (
                  <button
                    key={project.uuid}
                    type="button"
                    onClick={() => toggle(project.uuid)}
                    aria-pressed={isSelected}
                    className={clsx(
                      'w-full text-start flex items-start gap-3 rounded-md border px-3 py-2',
                      'transition-colors duration-200 ease-slide',
                      isSelected
                        ? 'border-champagne/60 bg-champagne/10'
                        : 'border-hairline-soft bg-surface-sunken/70 hover:border-champagne/40'
                    )}
                  >
                    <span
                      className={clsx(
                        'mt-0.5 flex h-4 w-4 flex-shrink-0 items-center justify-center rounded border',
                        isSelected ? 'border-champagne bg-champagne/20' : 'border-hairline'
                      )}
                    >
                      {isSelected && <PiCheckLight className="h-3 w-3 text-champagne" />}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block text-sm font-medium text-gray-700 dark:text-gray-200 truncate">
                        {project.title}
                      </span>
                      {project.description && (
                        <span className="mt-0.5 block text-xs text-fg-muted line-clamp-2">
                          {project.description}
                        </span>
                      )}
                      <span className="mt-1 flex items-center gap-2 text-[11px] text-fg-muted">
                        {project.status && <span className="capitalize">{project.status}</span>}
                        {project.created_at && <span>{formatDate(project.created_at)}</span>}
                      </span>
                    </span>
                  </button>
                );
              })}
            </div>
          )}
        </section>
      </div>

      <div className="flex-shrink-0 border-t border-hairline-soft px-5 py-4 flex justify-end gap-2">
        <Button variant="neutral" size="small" onClick={onClose}>
          {t('Close')}
        </Button>
        <Button
          variant="primary"
          size="small"
          onClick={handleLink}
          disabled={selected.length === 0 || isSaving}
          isLoading={isSaving}
        >
          {selected.length > 1
            ? t('Link to {{count}} projects', { count: selected.length })
            : t('Link to project')}
        </Button>
      </div>
    </div>
  );
};

export default ConnectToProjectPanel;
